import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AdminAuthContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminAuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('adminToken'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchCurrentAdmin();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchCurrentAdmin = async () => {
    try {
      // Check if staff data is stored (staff users)
      const storedStaff = localStorage.getItem('staffData');
      if (storedStaff) {
        const staffData = JSON.parse(storedStaff);
        setAdmin({ ...staffData, role: 'staff', userType: 'staff' });
        setLoading(false);
        return;
      }

      // Check if admin user data is stored in localStorage
      const storedUser = localStorage.getItem('adminUser');
      if (storedUser) {
        const userData = JSON.parse(storedUser);
        setAdmin(userData);
        setLoading(false);
        return;
      }

      // If no stored data, try to fetch from API (for admin only)
      const response = await axios.get(`${API}/admin/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdmin(response.data);
      localStorage.setItem('adminUser', JSON.stringify(response.data));
    } catch (error) {
      console.error('Failed to fetch admin:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (mobile, password) => {
    try {
      // All logins now use mobile number
      const response = await axios.post(`${API}/admin/auth/login`, { 
        mobile, 
        password 
      });
      
      const { token: newToken, admin: adminData } = response.data;
      
      localStorage.setItem('adminToken', newToken);
      
      // Handle staff users differently
      if (adminData.userType === 'staff') {
        localStorage.setItem('staffData', JSON.stringify({
          id: adminData.id,
          name: adminData.name,
          mobile: adminData.mobile,
          post: adminData.post,
          locationId: adminData.locationId,
          locationName: adminData.locationName
        }));
        localStorage.removeItem('adminUser');
      } else {
        localStorage.setItem('adminUser', JSON.stringify(adminData));
        localStorage.removeItem('staffData');
      }
      
      setToken(newToken);
      setAdmin(adminData);
      
      return { 
        success: true, 
        userType: adminData.userType,
        role: adminData.role
      };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Login failed' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    localStorage.removeItem('staffData');
    setToken(null);
    setAdmin(null);
  };

  return (
    <AdminAuthContext.Provider value={{ admin, token, login, logout, loading }}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return context;
};